import type {
  TBillingRecord,
  TInvoiceCustomer,
  TBillingHistoryItem,
  TSubscriptionDetailItem,
} from '@/types/subscription';

import { INVOICE_COMPANY_INFO } from '@/constants/constant';
import { ECurrency } from '@/enum';
import { generateInvoicesPdf } from '@/lib/invoiceTemplate';
import { downloadFile, formatUnixToLongDate } from '@/utils';

const CURRENCY_CONFIG: Record<
  ECurrency,
  { symbol: string; decimals: number; symbolAfter: boolean }
> = {
  [ECurrency.KWD]: { symbol: 'د.ك', decimals: 3, symbolAfter: true },
  [ECurrency.USD]: { symbol: '$', decimals: 2, symbolAfter: false },
};

export const formatCurrency = (amount: number, currency: string): string => {
  const currencyCode = currency?.toUpperCase() as ECurrency;
  const config = CURRENCY_CONFIG[currencyCode];
  if (!config) return `${amount.toFixed(2)} ${currency}`;
  const formatted = amount.toFixed(config.decimals);
  return config.symbolAfter
    ? `${formatted} ${config.symbol}`
    : `${config.symbol}${formatted}`;
};

export const buildBillingRecord = (
  row: TBillingHistoryItem | TSubscriptionDetailItem,
  formatDate: (value: number) => string
): TBillingRecord => {
  const dateLabel = formatDate(row.start_date);
  const id = 'id' in row ? row.id : row.invoiceName;
  const billingPeriod = `${formatUnixToLongDate(row.start_date)} - ${formatUnixToLongDate(row.end_date)}`;

  return {
    id,
    invoiceNumber: row.invoiceName,
    invoice: row.invoiceName,
    amount: formatCurrency(row.amount, row.currency),
    students: row.student_limit,
    date: dateLabel,
    dueDate: dateLabel,
    billingPeriod,
    status: 'Paid',
  };
};

export const downloadInvoices = async (
  records: TBillingRecord[],
  customer: TInvoiceCustomer,
  fileName: string
) => {
  if (records.length === 0) return;
  const blob = await generateInvoicesPdf(
    records,
    INVOICE_COMPANY_INFO,
    customer
  );
  downloadFile(blob, fileName);
};
